import { useState, useMemo } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Receipt, Tag, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DateRangePicker from "@/components/ui/date-range-picker";
import { formatCurrency } from "@/lib/format";
import {
  useListPriceAdjustments,
  type PriceAdjustmentTypeServer,
  type ListPriceAdjustmentsParams,
} from "@/api/payments";

const TYPE_OPTIONS: { value: PriceAdjustmentTypeServer; label: string }[] = [
  { value: "DISCOUNT", label: "Discount" },
  { value: "FEE", label: "Fee" },
  { value: "WEIGHT_ADJUSTMENT", label: "Weight adjustment" },
  { value: "ADDRESS_CHANGE", label: "Address change" },
  { value: "SERVICE_UPGRADE", label: "Service upgrade" },
  { value: "TAX_ADJUSTMENT", label: "Tax adjustment" },
  { value: "OTHER", label: "Other" },
];

const TYPE_BADGE_CLASS: Record<PriceAdjustmentTypeServer, string> = {
  DISCOUNT: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  FEE: "bg-rose-100 text-rose-800 hover:bg-rose-100",
  WEIGHT_ADJUSTMENT: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  ADDRESS_CHANGE: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  SERVICE_UPGRADE: "bg-violet-100 text-violet-800 hover:bg-violet-100",
  TAX_ADJUSTMENT: "bg-slate-100 text-slate-800 hover:bg-slate-100",
  OTHER: "bg-zinc-100 text-zinc-800 hover:bg-zinc-100",
};

const ANY_TYPE = "__ANY__";

export function PriceAdjustmentsTable() {
  const [type, setType] = useState<PriceAdjustmentTypeServer | typeof ANY_TYPE>(
    ANY_TYPE
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [orderId, setOrderId] = useState("");
  const [shipmentId, setShipmentId] = useState("");
  const [pickupBookingId, setPickupBookingId] = useState("");

  const params = useMemo<ListPriceAdjustmentsParams>(() => {
    const p: ListPriceAdjustmentsParams = { limit: 200 };
    if (type !== ANY_TYPE) p.type = type;
    if (orderId.trim()) p.orderId = orderId.trim();
    if (shipmentId.trim()) p.shipmentId = shipmentId.trim();
    if (pickupBookingId.trim()) p.pickupBookingId = pickupBookingId.trim();
    if (dateRange?.from) p.from = dateRange.from.toISOString();
    if (dateRange?.to) {
      // Inclusive end-of-day so a single-day range matches rows from that day.
      const end = new Date(dateRange.to);
      end.setHours(23, 59, 59, 999);
      p.to = end.toISOString();
    }
    return p;
  }, [type, orderId, shipmentId, pickupBookingId, dateRange]);

  const { data: rows, isLoading } = useListPriceAdjustments(params);

  const hasAnyFilter =
    type !== ANY_TYPE ||
    !!orderId ||
    !!shipmentId ||
    !!pickupBookingId ||
    !!dateRange?.from;

  const clearFilters = () => {
    setType(ANY_TYPE);
    setDateRange(undefined);
    setOrderId("");
    setShipmentId("");
    setPickupBookingId("");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="adj-type">Type</Label>
              <Select
                value={type}
                onValueChange={(v) =>
                  setType(v as PriceAdjustmentTypeServer | typeof ANY_TYPE)
                }
              >
                <SelectTrigger id="adj-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_TYPE}>Any type</SelectItem>
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date range</Label>
              <DateRangePicker
                date={dateRange}
                onDateChange={setDateRange}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj-order">Order ID</Label>
              <Input
                id="adj-order"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="ord_..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj-shipment">Shipment ID</Label>
              <Input
                id="adj-shipment"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="shp_..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj-booking">Pickup booking ID</Label>
              <Input
                id="adj-booking"
                value={pickupBookingId}
                onChange={(e) => setPickupBookingId(e.target.value)}
                placeholder="pb_..."
              />
            </div>

            {hasAnyFilter && (
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Adjustments
            {!isLoading && rows ? ` (${rows.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !rows || rows.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              <Receipt className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p>No adjustments match these filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Applied by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isCredit = row.amountCents < 0;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {format(new Date(row.createdAt), "MMM d, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={TYPE_BADGE_CLASS[row.type]}
                        >
                          {row.type.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium tabular-nums ${
                          isCredit ? "text-emerald-700" : "text-rose-700"
                        }`}
                      >
                        {isCredit ? "−" : "+"}
                        {formatCurrency(Math.abs(row.amountCents))}
                      </TableCell>
                      <TableCell className="text-sm">
                        <ScopeCell
                          orderId={row.orderId}
                          shipmentId={row.shipmentId}
                          shipmentNumber={row.shipment?.shipmentNumber ?? null}
                          pickupBookingId={row.pickupBookingId}
                        />
                      </TableCell>
                      <TableCell className="text-sm max-w-[280px] truncate">
                        {row.reason ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {row.appliedBy ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ScopeCell({
  orderId,
  shipmentId,
  shipmentNumber,
  pickupBookingId,
}: {
  orderId: string | null;
  shipmentId: string | null;
  shipmentNumber: string | null;
  pickupBookingId: string | null;
}) {
  const parts: string[] = [];
  if (shipmentNumber) parts.push(`SHP-${shipmentNumber}`);
  else if (shipmentId) parts.push(`shipment ${shipmentId.slice(0, 8)}`);
  if (pickupBookingId) parts.push(`pickup ${pickupBookingId.slice(0, 8)}`);
  if (orderId && parts.length === 0) parts.push(`order ${orderId.slice(0, 8)}`);
  if (parts.length === 0)
    return <span className="text-muted-foreground">—</span>;
  return <span>{parts.join(" · ")}</span>;
}
